import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import Logo from "../components/Logo";
import ThemeToggle from "../components/ThemeToggle";
import CountryCombobox from "../components/CountryCombobox";
import ImageUpload from "../components/ImageUpload";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  MEMBER_TYPES, BUSINESS_TYPES, PROFESSIONAL_TYPES, INVESTOR_TYPES,
  COMPANY_SIZES, BUSINESS_STAGES, INDUSTRIES, AVAILABILITY, INVESTOR_KINDS,
  INVESTMENT_STAGES, TICKET_RANGES, YEARS_EXPERIENCE, OFFERS, LOOKING_FOR,
} from "../data/hub";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  member_type: "",
  personal: { full_name: "", professional_headline: "", bio: "", country: "", city: "", linkedin_url: "", website: "", profile_photo: null },
  business: { name: "", logo: null, business_type: "", industry: "", country: "", city: "", website: "", company_size: "", stage: "", year_founded: "", description: "", products_services: "", markets: [] },
  professional: { professional_title: "", expertise: [], services: [], industries: [], years_experience: "", countries_markets: [], availability: "" },
  investor: { investor_type: "", organization: "", sectors: [], preferred_stages: [], geography: [], ticket_range: "", thesis: "", open_to_intros: false },
  offers: [],
  looking_for: [],
  looking_for_details: "",
  discoverable_preference: "not_yet",
  consents: { terms_accepted: false, privacy_accepted: false, marketing_opt_in: false },
};

function ChipToggle({ options, value = [], onChange, testIdPrefix }) {
  const toggle = (o) => onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const sel = value.includes(o);
        return (
          <button type="button" key={o} onClick={() => toggle(o)} data-testid={`${testIdPrefix}-${o}`} aria-pressed={sel}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
              sel ? "border-[color:var(--invert-bg)] bg-[color:var(--invert-bg)] text-white" :
              "border-[color:var(--hairline)] bg-[color:var(--surface)] text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"}`}>
            {sel && <Check className="h-3.5 w-3.5" />}{o}
          </button>
        );
      })}
    </div>
  );
}

function CsvInput({ value = [], onChange, placeholder, testId }) {
  const [text, setText] = useState(value.join(", "));
  return (
    <Input value={text}
      onChange={(e) => { setText(e.target.value); onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean)); }}
      placeholder={placeholder} data-testid={testId} className="h-11 rounded-xl" />
  );
}

export default function Onboarding() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // holds {profile_completion}
  const [form, setForm] = useState({
    ...emptyForm,
    personal: { ...emptyForm.personal, full_name: user?.name || "" },
  });

  const totalSteps = 6;
  const kind = form.member_type
    ? BUSINESS_TYPES.has(form.member_type) ? "business"
    : INVESTOR_TYPES.has(form.member_type) ? "investor"
    : PROFESSIONAL_TYPES.has(form.member_type) ? "professional"
    : "other"
    : "";

  const set = (updater) => setForm((f) => ({ ...f, ...(typeof updater === "function" ? updater(f) : updater) }));
  const setPersonal = (k, v) => set((f) => ({ personal: { ...f.personal, [k]: v } }));
  const setBusiness = (k, v) => set((f) => ({ business: { ...f.business, [k]: v } }));
  const setPro = (k, v) => set((f) => ({ professional: { ...f.professional, [k]: v } }));
  const setInv = (k, v) => set((f) => ({ investor: { ...f.investor, [k]: v } }));
  const setConsent = (k, v) => set((f) => ({ consents: { ...f.consents, [k]: v } }));

  const canNext = useMemo(() => {
    if (step === 1) return !!form.member_type;
    if (step === 2) return form.personal.full_name.trim() && form.personal.country;
    if (step === 3) {
      if (kind === "business") return form.business.name.trim() && form.business.industry;
      if (kind === "professional") return form.professional.professional_title.trim();
      if (kind === "investor") return form.investor.investor_type;
      return true; // "other" / educational — allow through
    }
    if (step === 4) return form.offers.length > 0 && form.looking_for.length > 0;
    if (step === 5) return form.consents.terms_accepted && form.consents.privacy_accepted;
    return true;
  }, [step, form, kind]);

  const submit = async () => {
    setSubmitting(true);
    const payload = {
      member_type: form.member_type,
      full_name: form.personal.full_name,
      professional_headline: form.personal.professional_headline,
      bio: form.personal.bio,
      country: form.personal.country,
      city: form.personal.city,
      linkedin_url: form.personal.linkedin_url,
      website: form.personal.website,
      profile_photo: form.personal.profile_photo,
      business: kind === "business" && form.business.name.trim() ? form.business : null,
      professional: kind === "professional" ? form.professional : null,
      investor: kind === "investor" ? form.investor : null,
      offers: form.offers,
      looking_for: form.looking_for,
      looking_for_details: form.looking_for_details,
      discoverable_preference: form.discoverable_preference,
      consents: form.consents,
    };
    try {
      const { data } = await axios.post(`${API}/hub/onboarding`, payload, { withCredentials: true });
      setDone({ profile_completion: data.profile.profile_completion });
      await refreshUser();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const goNext = () => {
    if (step === totalSteps - 1) { submit(); return; }
    setStep((s) => s + 1);
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[color:var(--paper)] px-5" data-testid="onboarding-complete">
        <Seo title="You're ready" path="/app/onboarding" />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="hub-card w-full max-w-xl rounded-3xl p-10 text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[color:var(--brand-green)]/12 text-[color:var(--brand-green)]"><Sparkles className="h-8 w-8" /></span>
          <h1 className="font-display mt-6 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Your Hub profile is ready.</h1>
          <p className="mt-4 text-[color:var(--ink-soft)]">You're now part of the StUpEco Hub. As the network grows, you'll be able to discover businesses, professionals, partnerships, events, discussions and opportunities relevant to you.</p>
          <div className="mt-8 rounded-2xl bg-[color:var(--soft)] p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-soft)]">Profile completion</p>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[color:var(--surface)]"><div className="h-full brand-bar" style={{ width: `${done.profile_completion}%` }} /></div>
            <p className="mt-2 text-right font-display text-2xl font-semibold text-[color:var(--ink)]" data-testid="completion-percent">{done.profile_completion}%</p>
          </div>
          <button onClick={() => navigate("/app", { replace: true })} data-testid="onboarding-enter-hub"
            className="group mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3.5 text-sm font-semibold text-white hover:-translate-y-0.5 transition-transform">
            Enter My Hub <ArrowRight className="h-4 w-4" />
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--paper)]" data-testid="onboarding-page">
      <Seo title="Onboarding" description="Complete your StUpEco Hub profile." path="/app/onboarding" />
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[color:var(--hairline)] bg-[color:var(--surface-glass)] px-5 backdrop-blur-xl sm:px-8">
        <Logo />
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold text-[color:var(--ink-soft)]" data-testid="onboarding-step-indicator">Step {step + 1} of {totalSteps}</span>
          <ThemeToggle />
        </div>
      </header>

      <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col px-5 py-8 sm:py-12">
        {/* Progress bar */}
        <div className="mb-8 h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--soft)]" aria-hidden="true">
          <motion.div className="brand-bar h-full" initial={{ width: 0 }} animate={{ width: `${((step + 1) / totalSteps) * 100}%` }} transition={{ duration: 0.4 }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }} className="flex-1">
            {step === 0 && (
              <div data-testid="step-welcome">
                <span className="eyebrow"><span className="brand-bar h-1.5 w-1.5 rounded-full" /> Welcome</span>
                <h1 className="font-display mt-4 text-4xl font-semibold leading-[1.02] tracking-[-0.02em] text-[color:var(--ink)] sm:text-5xl">Welcome to StUpEco Hub</h1>
                <p className="mt-5 max-w-xl text-[color:var(--ink-soft)] sm:text-lg">Tell us a little about yourself so we can shape your Hub experience around what you do, what you offer and what you're looking for.</p>
                <div className="mt-8 flex flex-wrap items-center gap-2 text-sm font-semibold text-[color:var(--ink)]">
                  {["Discover","Connect","Collaborate","Grow"].map((w, i, a) => (
                    <span key={w} className="flex items-center gap-2">
                      <span className="rounded-full bg-[color:var(--soft)] px-3.5 py-1.5">{w}</span>
                      {i < a.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-[color:var(--ink-soft)]" />}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div data-testid="step-member-type">
                <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">How would you primarily describe yourself?</h2>
                <p className="mt-3 text-[color:var(--ink-soft)]">Pick the option that fits you best right now. You can refine this later.</p>
                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {MEMBER_TYPES.map((t) => {
                    const sel = form.member_type === t;
                    return (
                      <button key={t} type="button" onClick={() => set({ member_type: t })} data-testid={`member-type-${t}`}
                        className={`rounded-2xl border p-4 text-left transition-all ${sel ? "border-[color:var(--invert-bg)] bg-[color:var(--soft)] shadow-[0_2px_10px_rgba(10,10,10,0.06)]" : "border-[color:var(--hairline)] bg-[color:var(--surface)] hover:border-[color:var(--ink)]"}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-[color:var(--ink)]">{t}</span>
                          {sel && <Check className="h-4 w-4 text-[color:var(--brand-green)]" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-personal">
                <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Tell us about yourself</h2>
                <p className="mt-3 text-[color:var(--ink-soft)]">A short personal profile — you can polish it later.</p>
                <div className="mt-8 space-y-6">
                  <ImageUpload value={form.personal.profile_photo} onChange={(v) => setPersonal("profile_photo", v)} testId="ob-photo" />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2"><Label>Full name <span className="text-[color:var(--brand-red)]">*</span></Label>
                      <Input value={form.personal.full_name} onChange={(e) => setPersonal("full_name", e.target.value)} placeholder="Jane Doe" className="h-11 rounded-xl" data-testid="ob-full-name" /></div>
                    <div className="space-y-2"><Label>Professional headline</Label>
                      <Input value={form.personal.professional_headline} onChange={(e) => setPersonal("professional_headline", e.target.value)} placeholder="e.g. Growth Consultant helping startups expand across UAE & India" className="h-11 rounded-xl" data-testid="ob-headline" /></div>
                    <div className="space-y-2"><Label>Country <span className="text-[color:var(--brand-red)]">*</span></Label>
                      <CountryCombobox value={form.personal.country} onChange={(v) => setPersonal("country", v)} testIdPrefix="ob-country" /></div>
                    <div className="space-y-2"><Label>City</Label>
                      <Input value={form.personal.city} onChange={(e) => setPersonal("city", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" data-testid="ob-city" /></div>
                    <div className="space-y-2"><Label>LinkedIn URL</Label>
                      <Input value={form.personal.linkedin_url} onChange={(e) => setPersonal("linkedin_url", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" data-testid="ob-linkedin" /></div>
                    <div className="space-y-2"><Label>Personal / professional website</Label>
                      <Input value={form.personal.website} onChange={(e) => setPersonal("website", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" data-testid="ob-website" /></div>
                  </div>
                  <div className="space-y-2"><Label>Short bio</Label>
                    <Textarea value={form.personal.bio} onChange={(e) => setPersonal("bio", e.target.value)} placeholder="A sentence or two about what you do." className="min-h-28 rounded-xl" data-testid="ob-bio" /></div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="step-conditional">
                {kind === "business" && (
                  <>
                    <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">About your business</h2>
                    <p className="mt-3 text-[color:var(--ink-soft)]">Tell us about the business you'll represent on the Hub.</p>
                    <div className="mt-8 space-y-6">
                      <ImageUpload kind="logo" value={form.business.logo} onChange={(v) => setBusiness("logo", v)} testId="ob-logo" />
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2"><Label>Business name <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Input value={form.business.name} onChange={(e) => setBusiness("name", e.target.value)} className="h-11 rounded-xl" data-testid="ob-business-name" /></div>
                        <div className="space-y-2"><Label>Industry <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Select value={form.business.industry} onValueChange={(v) => setBusiness("industry", v)}>
                            <SelectTrigger data-testid="ob-industry" className="h-11 rounded-xl"><SelectValue placeholder="Select industry" /></SelectTrigger>
                            <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                          </Select></div>
                        <div className="space-y-2"><Label>Country</Label>
                          <CountryCombobox value={form.business.country} onChange={(v) => setBusiness("country", v)} testIdPrefix="ob-business-country" /></div>
                        <div className="space-y-2"><Label>City</Label>
                          <Input value={form.business.city} onChange={(e) => setBusiness("city", e.target.value)} className="h-11 rounded-xl" /></div>
                        <div className="space-y-2"><Label>Website</Label>
                          <Input value={form.business.website} onChange={(e) => setBusiness("website", e.target.value)} placeholder="https://" className="h-11 rounded-xl" /></div>
                        <div className="space-y-2"><Label>Year founded</Label>
                          <Input value={form.business.year_founded} onChange={(e) => setBusiness("year_founded", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" /></div>
                        <div className="space-y-2"><Label>Company size</Label>
                          <Select value={form.business.company_size} onValueChange={(v) => setBusiness("company_size", v)}>
                            <SelectTrigger data-testid="ob-company-size" className="h-11 rounded-xl"><SelectValue placeholder="Select size" /></SelectTrigger>
                            <SelectContent>{COMPANY_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select></div>
                        <div className="space-y-2"><Label>Business stage</Label>
                          <Select value={form.business.stage} onValueChange={(v) => setBusiness("stage", v)}>
                            <SelectTrigger data-testid="ob-business-stage" className="h-11 rounded-xl"><SelectValue placeholder="Select stage" /></SelectTrigger>
                            <SelectContent>{BUSINESS_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select></div>
                      </div>
                      <div className="space-y-2"><Label>Short description</Label>
                        <Textarea value={form.business.description} onChange={(e) => setBusiness("description", e.target.value)} className="min-h-24 rounded-xl" /></div>
                      <div className="space-y-2"><Label>Products / services</Label>
                        <Textarea value={form.business.products_services} onChange={(e) => setBusiness("products_services", e.target.value)} placeholder="What do you offer to customers?" className="min-h-20 rounded-xl" /></div>
                      <div className="space-y-2"><Label>Markets served</Label>
                        <CsvInput value={form.business.markets} onChange={(v) => setBusiness("markets", v)} placeholder="e.g. UAE, India, UK (comma-separated)" testId="ob-markets" /></div>
                    </div>
                  </>
                )}
                {kind === "professional" && (
                  <>
                    <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Your professional profile</h2>
                    <p className="mt-3 text-[color:var(--ink-soft)]">Tell us how you help clients.</p>
                    <div className="mt-8 space-y-6">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2"><Label>Professional title <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Input value={form.professional.professional_title} onChange={(e) => setPro("professional_title", e.target.value)} className="h-11 rounded-xl" data-testid="ob-pro-title" /></div>
                        <div className="space-y-2"><Label>Years of experience</Label>
                          <Select value={form.professional.years_experience} onValueChange={(v) => setPro("years_experience", v)}>
                            <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{YEARS_EXPERIENCE.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
                          </Select></div>
                        <div className="space-y-2"><Label>Availability</Label>
                          <Select value={form.professional.availability} onValueChange={(v) => setPro("availability", v)}>
                            <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{AVAILABILITY.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                          </Select></div>
                      </div>
                      <div className="space-y-2"><Label>Areas of expertise</Label>
                        <CsvInput value={form.professional.expertise} onChange={(v) => setPro("expertise", v)} placeholder="Comma-separated (e.g. Growth, SEO, GTM)" testId="ob-expertise" /></div>
                      <div className="space-y-2"><Label>Services offered</Label>
                        <CsvInput value={form.professional.services} onChange={(v) => setPro("services", v)} placeholder="Comma-separated" testId="ob-services" /></div>
                      <div className="space-y-2"><Label>Industries served</Label>
                        <ChipToggle options={INDUSTRIES} value={form.professional.industries} onChange={(v) => setPro("industries", v)} testIdPrefix="ob-pro-industry" /></div>
                      <div className="space-y-2"><Label>Countries / markets served</Label>
                        <CsvInput value={form.professional.countries_markets} onChange={(v) => setPro("countries_markets", v)} placeholder="Comma-separated" testId="ob-pro-markets" /></div>
                    </div>
                  </>
                )}
                {kind === "investor" && (
                  <>
                    <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Your investment profile</h2>
                    <p className="mt-3 text-[color:var(--ink-soft)]">Only what you're comfortable sharing.</p>
                    <div className="mt-8 space-y-6">
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-2"><Label>Investor type <span className="text-[color:var(--brand-red)]">*</span></Label>
                          <Select value={form.investor.investor_type} onValueChange={(v) => setInv("investor_type", v)}>
                            <SelectTrigger data-testid="ob-investor-type" className="h-11 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent>{INVESTOR_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                          </Select></div>
                        <div className="space-y-2"><Label>Organization</Label>
                          <Input value={form.investor.organization} onChange={(e) => setInv("organization", e.target.value)} placeholder="Optional" className="h-11 rounded-xl" /></div>
                        <div className="space-y-2"><Label>Typical ticket range</Label>
                          <Select value={form.investor.ticket_range} onValueChange={(v) => setInv("ticket_range", v)}>
                            <SelectTrigger className="h-11 rounded-xl"><SelectValue placeholder="Select range" /></SelectTrigger>
                            <SelectContent>{TICKET_RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                          </Select></div>
                      </div>
                      <div className="space-y-2"><Label>Investment sectors</Label>
                        <ChipToggle options={INDUSTRIES} value={form.investor.sectors} onChange={(v) => setInv("sectors", v)} testIdPrefix="ob-inv-sector" /></div>
                      <div className="space-y-2"><Label>Preferred startup stages</Label>
                        <ChipToggle options={INVESTMENT_STAGES} value={form.investor.preferred_stages} onChange={(v) => setInv("preferred_stages", v)} testIdPrefix="ob-inv-stage" /></div>
                      <div className="space-y-2"><Label>Investment geography</Label>
                        <CsvInput value={form.investor.geography} onChange={(v) => setInv("geography", v)} placeholder="Comma-separated (e.g. UAE, KSA, India)" testId="ob-inv-geo" /></div>
                      <div className="space-y-2"><Label>Investment thesis</Label>
                        <Textarea value={form.investor.thesis} onChange={(e) => setInv("thesis", e.target.value)} className="min-h-24 rounded-xl" /></div>
                      <label className="flex items-start gap-3 text-sm text-[color:var(--ink)]">
                        <Checkbox checked={form.investor.open_to_intros} onCheckedChange={(v) => setInv("open_to_intros", !!v)} data-testid="ob-inv-intros" className="mt-0.5" />
                        <span>Open to warm introductions from Hub members.</span>
                      </label>
                    </div>
                  </>
                )}
                {kind === "other" && (
                  <div className="rounded-2xl bg-[color:var(--soft)] p-8 text-center">
                    <p className="text-[color:var(--ink)]">No extra details needed for your member type. Continue to the Hub Intent step.</p>
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div data-testid="step-intent">
                <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">What can you offer, and what are you looking for?</h2>
                <p className="mt-3 text-[color:var(--ink-soft)]">This shapes future discovery, recommendations and cross-selling matches.</p>
                <div className="mt-8 space-y-8">
                  <div className="space-y-3">
                    <Label>What can you offer to the Hub? <span className="text-[color:var(--brand-red)]">*</span></Label>
                    <ChipToggle options={OFFERS} value={form.offers} onChange={(v) => set({ offers: v })} testIdPrefix="ob-offer" />
                  </div>
                  <div className="space-y-3">
                    <Label>What are you looking for? <span className="text-[color:var(--brand-red)]">*</span></Label>
                    <ChipToggle options={LOOKING_FOR} value={form.looking_for} onChange={(v) => set({ looking_for: v })} testIdPrefix="ob-looking" />
                  </div>
                  <div className="space-y-2"><Label>Anything specific you're looking for?</Label>
                    <Textarea value={form.looking_for_details} onChange={(e) => set({ looking_for_details: e.target.value })} placeholder="e.g. UAE-based agencies serving SME clients for referral partnerships." className="min-h-24 rounded-xl" data-testid="ob-looking-details" /></div>
                </div>
              </div>
            )}

            {step === 5 && (
              <div data-testid="step-membership">
                <h2 className="font-display text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Membership & visibility</h2>
                <p className="mt-3 text-[color:var(--ink-soft)]">You're joining as a Free member — you can upgrade any time. Nothing is charged.</p>

                <div className="mt-8 hub-card rounded-2xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--brand-teal)]">Current membership</p>
                      <p className="font-display mt-1 text-2xl font-semibold text-[color:var(--ink)]">Free</p>
                      <p className="mt-1 text-sm text-[color:var(--ink-soft)]">Explore the Hub.</p>
                    </div>
                    <a href="/membership" target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-[color:var(--ink)] underline-offset-4 hover:underline" data-testid="ob-explore-memberships">Explore memberships</a>
                  </div>
                </div>

                <div className="mt-8 space-y-3">
                  <Label>Would you like your profile to become discoverable when the StUpEco Hub directory launches?</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[{ id: "yes", label: "Yes, list me" }, { id: "not_yet", label: "Not yet" }].map((o) => (
                      <button key={o.id} type="button" onClick={() => set({ discoverable_preference: o.id })} data-testid={`ob-discover-${o.id}`}
                        className={`rounded-2xl border p-4 text-left text-sm font-semibold transition-all ${form.discoverable_preference === o.id ? "border-[color:var(--invert-bg)] bg-[color:var(--soft)]" : "border-[color:var(--hairline)] bg-[color:var(--surface)] hover:border-[color:var(--ink)]"}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[color:var(--ink-soft)]">Choosing Yes does not immediately publish anything. The public directory is not live yet.</p>
                </div>

                <div className="mt-8 space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-soft)]">Consents</p>
                  <label className="flex items-start gap-3 text-sm text-[color:var(--ink)]">
                    <Checkbox checked={form.consents.terms_accepted} onCheckedChange={(v) => setConsent("terms_accepted", !!v)} data-testid="ob-consent-terms" className="mt-0.5" />
                    <span>I accept the StUpEco Hub Terms of Use. <span className="text-[color:var(--brand-red)]">*</span></span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-[color:var(--ink)]">
                    <Checkbox checked={form.consents.privacy_accepted} onCheckedChange={(v) => setConsent("privacy_accepted", !!v)} data-testid="ob-consent-privacy" className="mt-0.5" />
                    <span>I accept the Privacy Policy. <span className="text-[color:var(--brand-red)]">*</span></span>
                  </label>
                  <label className="flex items-start gap-3 text-sm text-[color:var(--ink-soft)]">
                    <Checkbox checked={form.consents.marketing_opt_in} onCheckedChange={(v) => setConsent("marketing_opt_in", !!v)} data-testid="ob-consent-marketing" className="mt-0.5" />
                    <span>Send me product updates, opportunities and StUpEco announcements. (Optional)</span>
                  </label>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Nav buttons */}
        <div className="mt-10 flex items-center justify-between border-t border-[color:var(--hairline)] pt-6">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0 || submitting} data-testid="onboarding-back"
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--hairline)] bg-[color:var(--surface)] px-5 py-2.5 text-sm font-semibold text-[color:var(--ink)] disabled:opacity-40">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <button onClick={goNext} disabled={!canNext || submitting} data-testid="onboarding-next"
            className="group inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0">
            {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : step === 0 ? "Build My Hub Profile" : step === totalSteps - 1 ? "Complete" : "Continue"}
            {!submitting && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
